import api from './api';
import { Pagination, StoreSellerAdmin, CreateStoreSellerRequest, UpdateStoreSellerRequest, UserByRole, AdminCategory, AdminPriceTable, UpdateCategoryRequest, AdminProduct, AdminProductFilter, SiteImage, AdminCollection, AdminCollectionFilter, CreateCollectionRequest, UpdateCollectionRequest, CollectionImageResult, HomeBanner, CreateHomeBannerRequest, UpdateHomeBannerRequest, HomeBannerImageResult } from '../models';
import { AdminProductImage, AdminProductDetail, AdminSupplier } from '../models/product';

export const adminService = {
  // Products
  getProducts: (filter: AdminProductFilter) =>
    api.get<Pagination<AdminProduct>>('/products', { params: filter }).then((r) => r.data),

  getProductById: (id: number) =>
    api.get<AdminProductDetail>(`/products/${id}`).then((r) => r.data),

  // Suppliers
  getSuppliers: (filter?: { pageNumber?: number; pageSize?: number }) =>
    api
      .get<Pagination<AdminSupplier>>('/suppliers', { params: filter ?? { pageNumber: 1, pageSize: 200 } })
      .then((r) => r.data),

  // Users
  getUsersByRole: (roleId: number) =>
    api.get<UserByRole[]>(`/users/by-role/${roleId}`).then((r) => r.data),

  // StoreSellers
  getSellers: (params?: { page?: number; pageSize?: number; search?: string }) =>
    api.get<Pagination<StoreSellerAdmin>>('/store-sellers', { params }).then((r) => r.data),

  createSeller: (data: CreateStoreSellerRequest) =>
    api.post<number>('/store-sellers', data).then((r) => r.data),

  updateSeller: (id: number, data: UpdateStoreSellerRequest) =>
    api.put<void>(`/store-sellers/${id}`, data).then((r) => r.data),

  deleteSeller: (id: number) =>
    api.delete(`/store-sellers/${id}`).then((r) => r.data),

  // ProductImages
  getProductImages: (productId: number) =>
    api.get<AdminProductImage[]>(`/products/${productId}/images`).then((r) => r.data),

  uploadProductImage: (productId: number, file: File, colorId?: number | null) => {
    const form = new FormData();
    form.append('file', file);
    if (colorId !== undefined && colorId !== null) {
      form.append('colorId', String(colorId));
    }
    return api
      .post<void>(`/products/${productId}/images`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deleteProductImage: (productId: number, imageId: number) =>
    api.delete(`/products/${productId}/images/${imageId}`).then((r) => r.data),

  setMainProductImage: (productId: number, imageId: number) =>
    api.put<void>(`/products/${productId}/images/${imageId}/main`).then((r) => r.data),

  setProductImageColor: (productId: number, imageId: number, colorId: number) =>
    api.put<void>(`/products/${productId}/images/${imageId}/color`, { colorId }).then((r) => r.data),

  reorderProductImages: (productId: number, items: { imageId: number; order: number }[]) =>
    api.put<void>(`/products/${productId}/images/reorder`, items).then((r) => r.data),

  // Categories
  getCategories: () =>
    api.get<Pagination<AdminCategory>>('/categories?pageNumber=1&pageSize=1000').then((r) => r.data),

  updateCategory: (id: number, data: UpdateCategoryRequest) =>
    api.put(`/categories/${id}`, data).then((r) => r.data),

  bulkSetShowOnHome: (categoryIds: number[], showOnHome: boolean) =>
    api.put('/categories/bulk-home', { categoryIds, showOnHome }).then((r) => r.data),

  bulkSetPriceTable: (categoryIds: number[], priceTableId: number | null) =>
    api.put('/categories/bulk-price-table', { categoryIds, priceTableId }).then((r) => r.data),

  // PriceTables
  getPriceTables: () =>
    api.get<Pagination<AdminPriceTable>>('/price-tables?pageNumber=1&pageSize=100').then((r) => r.data),

  // Site Images
  getHomeImages: () =>
    api.get<SiteImage[]>('/site-images').then((r) => r.data),

  uploadHomeImage: (key: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<void>(`/site-images/${key}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deleteHomeImage: (key: string) =>
    api.delete(`/site-images/${key}`).then((r) => r.data),

  // Collections
  getCollections: (filter?: AdminCollectionFilter) =>
    api
      .get<Pagination<AdminCollection>>('/collections', {
        params: {
          pageNumber: filter?.pageNumber ?? 1,
          pageSize: filter?.pageSize ?? 50,
          name: filter?.name,
        },
      })
      .then((r) => r.data),

  createCollection: (data: CreateCollectionRequest) =>
    api.post<AdminCollection>('/collections', data).then((r) => r.data),

  updateCollection: (id: number, data: UpdateCollectionRequest) =>
    api.put<AdminCollection>(`/collections/${id}`, data).then((r) => r.data),

  deleteCollection: (id: number) =>
    api.delete(`/collections/${id}`).then((r) => r.data),

  uploadCollectionImage: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<CollectionImageResult>(`/collections/${id}/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deleteCollectionImage: (id: number) =>
    api.delete(`/collections/${id}/image`).then((r) => r.data),

  setCollectionFeaturedInHero: (id: number) =>
    api.put<void>(`/collections/${id}/featured-in-hero`).then((r) => r.data),

  // Home Banners
  getBanners: () =>
    api.get<HomeBanner[]>('/home-banners').then((r) => r.data),

  createBanner: (data: CreateHomeBannerRequest) =>
    api.post<HomeBanner>('/home-banners', data).then((r) => r.data),

  updateBanner: (id: number, data: UpdateHomeBannerRequest) =>
    api.put<HomeBanner>(`/home-banners/${id}`, data).then((r) => r.data),

  deleteBanner: (id: number) =>
    api.delete(`/home-banners/${id}`).then((r) => r.data),

  uploadBannerImage: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<HomeBannerImageResult>(`/home-banners/${id}/image`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deleteBannerImage: (id: number) =>
    api.delete(`/home-banners/${id}/image`).then((r) => r.data),
};
